import React, { useEffect, useState } from 'react';
import styles from './InputDropdown.module.scss';
import { InputContainer } from '../InputContainer/InputContainer';
//translations
import { tr } from '../../../../utils/translations/translator';
import { useTranslation } from 'react-i18next';
import { OutsideAlerter } from '../OutsideAlerter/OutsideAlerter';
import classnames from 'classnames';
import { Validity } from '../../interfaces/tip/Validations';
import { InputHeader } from '../InputHeader/InputHeader';
import { Sport } from '../../interfaces/utils';
import { toast } from 'react-toastify';
import { Market, MarketsBySport } from '../../../../store/markets/reducer';
import { isUnique } from './utils';

interface InputDropdownProps {
  label: string;
  markets: Market[];
  marketsBySport: MarketsBySport;
  featuredMarkets: Market[];
  featuredMarketsBySport: MarketsBySport;
  handleChange: (market: any) => void;
  value?: any;
  DropdownItem: React.FC<any>;
  placeholder?: string;
  openUpwards?: boolean;
  DropdownTitle?: string;
  validity?: Validity | false;
  sport: Sport | undefined;
}

export const MarketInputDropdown: React.FC<InputDropdownProps> = ({
  label,
  markets,
  featuredMarkets,
  handleChange,
  value,
  DropdownItem,
  placeholder = ' ',
  openUpwards = false,
  DropdownTitle,
  validity,
  sport,
  marketsBySport,
  featuredMarketsBySport,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const cx = classnames.bind(styles);
  const [previousSport, setPreviousSport] = useState<string | undefined>(
    sport ? sport.code : undefined,
  );
  const [filteredValue, setFilteredValue] = useState(value);
  const [formattedOptions, setFormattedOptions] = useState<any>(markets);
  const [formattedFeaturedOptions, setFormattedFeaturedOptions] =
    useState(featuredMarkets);

  const handleSelect = (market: Market) => {
    handleChange(market);
    setIsDropdownOpen(false);
  };

  const filterOptionsBySport = (
    markets: Market[],
    marketsBySport: MarketsBySport,
  ) => {
    const featured: Market[] = filterFeaturedOptionsBySport(
      featuredMarkets!,
      featuredMarketsBySport,
    );

    if (sport && sport.code) {
      const filteredDuplicates = marketsBySport[sport.code].filter((market) =>
        isUnique(market, featured),
      );
      return filteredDuplicates.map((market) => {
        return market;
      });
    }
    return markets.filter((market) => isUnique(market.code, featured));
  };
  const filterFeaturedOptionsBySport = (
    featuredMarkets: Market[],
    featuredMarketsBySport: MarketsBySport,
  ) => {
    if (sport && sport.code) {
      if (featuredMarketsBySport[sport.code]) {
        return featuredMarketsBySport[sport.code].map((featuredMarket) => {
          return {
            code: featuredMarket,
          };
        });
      } else {
        return [];
      }
    }
    return featuredMarkets;
  };
  const handleFormattedOptions = () => {
    setFormattedOptions(filterOptionsBySport(markets, marketsBySport));
    setFormattedFeaturedOptions(
      filterFeaturedOptionsBySport(featuredMarkets, featuredMarketsBySport),
    );
  };
  useEffect(() => {
    handleFormattedOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport]);

  const filterValueBySport = (value: Market) => {
    if (sport && sport.code && previousSport !== sport.code) {
      const filteredFeatured = filterFeaturedOptionsBySport(
        featuredMarkets,
        featuredMarketsBySport,
      );
      const filteredOptions = filterOptionsBySport(markets, marketsBySport);
      setPreviousSport(sport.code);
      if (filteredFeatured.length) {
        if (filteredFeatured[0] !== value) {
          sport && sport.code && toast.success(tr(t, 'market-change_toast'));
          handleChange(filteredFeatured[0]);
          return filteredFeatured[0];
        }
      } else if (filteredOptions.length) {
        if (filteredOptions[0] !== value) {
          sport && sport.code && toast.success(tr(t, 'market-change_toast'));
          handleChange(filteredOptions[0]);
          return filteredOptions[0];
        }
      }
    }

    return value;
  };
  useEffect(() => {
    setFilteredValue(filterValueBySport(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, value]);

  const renderOptions = (markets: Market[]) =>
    markets.map((market, index) => {
      return (
        <DropdownItem
          key={market.code || `market-${index}`}
          option={market}
          onClick={handleSelect}
          value={formatValue(market)}
        />
      );
    });

  const formatValue = (value: Market) => {
    return t(`markets:bet_markets_${value.code}`).replace('bet_markets_', '');
  };

  return (
    <>
      <InputContainer label={label} isDropdown validity={validity}>
        <div
          onClick={() => {
            setIsDropdownOpen(!isDropdownOpen);
          }}
          className={styles.InputDropdown}
        >
          <p>
            {value ? (
              formatValue(filteredValue)
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </p>
          {isDropdownOpen && (
            <OutsideAlerter
              onBlur={() => {
                setIsDropdownOpen(false);
              }}
            >
              <div
                className={cx(
                  styles.Dropdown,
                  openUpwards ? styles.openUpwards : styles.unset,
                )}
              >
                {DropdownTitle && (
                  <div className={styles.DropdownHeader}>
                    <label>{DropdownTitle}</label>
                    <button onClick={() => setIsDropdownOpen(false)}>OK</button>
                  </div>
                )}
                {formattedFeaturedOptions &&
                  !!formattedFeaturedOptions.length && (
                    <>
                      <div className={styles.DropdownTitle}>
                        <InputHeader title={tr(t, 'most-used-by-you')} />
                      </div>
                      {renderOptions(formattedFeaturedOptions)}
                    </>
                  )}
                <div className={styles.DropdownTitle}>
                  <InputHeader title={tr(t, 'most-used-by-others')} />
                </div>
                {renderOptions(formattedOptions)}
              </div>
            </OutsideAlerter>
          )}
        </div>
      </InputContainer>
    </>
  );
};
